import { useRef, useState } from 'react';

import { Button } from '@components/ui';
import { AbakoLogoSmall } from '@/components/shared/AbakoLogoSmall';

import { WelcomeStep1 } from './steps/WelcomeStep1';
import { WelcomeStep2 } from './steps/WelcomeStep2';
import { WelcomeStep3 } from './steps/WelcomeStep3';
import { WelcomeStep4 } from './steps/WelcomeStep4';
import { WelcomeStep5 } from './steps/WelcomeStep5';

import type { WelcomeStepRef } from './steps/types';

interface WelcomeIntroProps {
  onContinue: () => void;
}

export const WelcomeIntro = ({ onContinue }: WelcomeIntroProps) => {
  const [step, setStep] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  const stepRef = useRef<WelcomeStepRef>(null);

  const steps = [WelcomeStep1, WelcomeStep2, WelcomeStep3, WelcomeStep4, WelcomeStep5];
  const CurrentStep = steps[step] ?? WelcomeStep1;

  const isLastStep = step === steps.length - 1;

  const handleNext = async () => {
    if (isAnimating) return;

    setIsAnimating(true);

    try {
      await stepRef.current?.animateOut();

      if (isLastStep) {
        onContinue();
        return;
      }

      setStep((prevStep) => prevStep + 1);
    } finally {
      setIsAnimating(false);
    }
  };

  return (
    <div className="relative w-full h-full xl:py-[--spacing-8] flex bg-[url('/images/abako-bg-pattern.png')] bg-center bg-cover">
      <AbakoLogoSmall className="absolute z-10 top-0 left-0 p-4" />

      <div
        className="
          bg-green-500/0
          w-[918px] h-full max-h-[85dvh] min-h-fit m-auto
          flex flex-col items-center justify-between gap-[--spacing-7] text-center
        "
      >
        <CurrentStep ref={stepRef} />

        <Button
          type="button"
          onClick={handleNext}
          disabled={isAnimating}
          className="
            h-[44px] min-h-[44px] w-full max-w-[790px]
            text-[--text-dark-primary] bg-[radial-gradient(43.04%_97.91%_at_50%_48.86%,#059467_0%,#036347_100%)]
            hover:bg-none hover:bg-[--base-fill-1] hover:border-[--state-brand-active]
          "
        >
          {isLastStep ? 'Begin' : 'Continue'}
        </Button>
      </div>
    </div>
  );
};
